import { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import HttpService from "../../services/http";
import PaymentModuleService from "../../services/paymentModule";
import { toast } from "react-toastify";
import { queryKeys } from "../../constants/queryKeys";
import { queryClient } from "../../providers/QueryProvider";
import { forgetOwnerData } from "../entity/ownerData";

type SendMessagesToAllProps = {
  customer_identification: string;
  entity_identification: string;
  group_identification: string;
  data: any;
};

type SendMessageProps = {
  customer_identification: string;
  entity_identification: string;
  toSendMsgUserId: string;
  data: any;
};

type CorrectMessageProps = {
  customer_identification: string;
  entity_identification: string;
  message: string;
};

type EmailPreviewProps = {
  entity_user_identification: string;
  entity_identification: string;
  template: string;
  data: any;
};

type SendSMSProps = {
  customer_identification: string;
  entity_identification: string;
  user_identification?: string;
  group_identification?: string;
  data: {
    element: any;
    message: string;
    from?: string;
  };
};

export const useSendMessagesToAll = () => {
  return useMutation<SendMessagesToAllProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendMessagesToAllProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/message/bulk`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        toast.success("Message sent to all successfully");
        queryClient.invalidateQueries([queryKeys.messages]);
        queryClient.invalidateQueries([
          queryKeys.emailQuota,
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendMessagesToUsers = () => {
  return useMutation<SendMessagesToAllProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendMessagesToAllProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/message`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        toast.success("Message sent to users successfully");
        queryClient.invalidateQueries([queryKeys.messages]);
        queryClient.invalidateQueries([
          queryKeys.emailQuota,
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendMessageToOne = () => {
  return useMutation<SendMessageProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      toSendMsgUserId,
      data,
    }: SendMessageProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${toSendMsgUserId}/message`,
        data
      ),
    {
      onSuccess: () => {
        toast.success("Message sent.");
        queryClient.invalidateQueries([queryKeys.messages]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useCorrectMessage = () => {
  return useMutation<any, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      message,
    }: CorrectMessageProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/correct-message`,
        { message }
      ),
    {
      onSuccess: () => {},
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useEmailPreview = () => {
  return useMutation<EmailPreviewProps, AxiosError<any, any>, any>(
    ({
      entity_user_identification,
      entity_identification,
      template,
      data,
    }: EmailPreviewProps) =>
      HttpService.post(
        `/${entity_user_identification}/${entity_identification}/entity/template/${template}/preview`,
        data
      ),
    {
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendSMS = () => {
  return useMutation<SendSMSProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      data,
    }: SendSMSProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/sms`,
        data
      ),
    {
      onSuccess: (value, variables) => {
        forgetOwnerData(
          variables.customer_identification,
          variables.entity_identification,
          "sms_credit"
        );

        toast.success("SMS sent successfully");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendBulkSMS = () => {
  return useMutation<SendSMSProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendSMSProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/sms/bulk`,
        data
      ),
    {
      onSuccess: (value, variables) => {
        forgetOwnerData(
          variables.customer_identification,
          variables.entity_identification,
          "sms_credit"
        );

        if (
          value?.data?.element?.total_failed > 0 ||
          value?.data?.element?.total_blocked > 0
        ) {
          toast.warn(
            `SMS didnt send to ${
              value?.data?.element?.total_failed +
              value?.data?.element?.total_blocked
            } user(s)`
          );
        } else {
          toast.success("SMS sent to all users successfully");
        }
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendUsersBulkSMS = () => {
  return useMutation<SendSMSProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendSMSProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/users/sms/bulk`,
        data
      ),
    {
      onSuccess: (value, variables) => {
        forgetOwnerData(
          variables.customer_identification,
          variables.entity_identification,
          "sms_credit"
        );

        if (
          value?.data?.element?.total_failed > 0 ||
          value?.data?.element?.total_blocked > 0
        ) {
          toast.warn(
            `SMS didnt send to ${
              value?.data?.element?.total_failed +
              value?.data?.element?.total_blocked
            } user(s)`
          );
        } else {
          toast.success("SMS sent to all selected users successfully");
        }
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

type SendWhatsAppProps = {
  customer_identification: string;
  entity_identification: string;
  user_identification?: string;
  group_identification?: string;
  data: {
    template_key: string;
    language: string;
    variables: Record<string, string>;
    media_url?: string | null;
    schedule?: string;
    users?: Array<string>;
  };
};

const invalidateWhatsappCredit = (
  customer_identification: string,
  entity_identification: string
) => {
  forgetOwnerData(
    customer_identification,
    entity_identification,
    "whatsapp_credit"
  );
  queryClient.invalidateQueries(["whatsapp-config", entity_identification]);
};

const warnOnPartialWhatsApp = (value: any, successMessage: string) => {
  const failed = value?.data?.element?.total_failed ?? 0;
  const skipped = value?.data?.element?.total_skipped ?? 0;
  if (failed > 0 || skipped > 0) {
    toast.warn(`WhatsApp not sent to ${failed + skipped} user(s)`);
  } else {
    toast.success(successMessage);
  }
};

export const useGetWhatsappConfig = (
  customer_identification: string,
  entity_identification: string,
  enabled = true
) => {
  return useQuery(
    ["whatsapp-config", entity_identification],
    async () => {
      const response = await HttpService.get(
        `/${customer_identification}/${entity_identification}/user/whatsapp/config`
      );
      return response?.data?.element;
    },
    {
      enabled: enabled && !!customer_identification && !!entity_identification,
      staleTime: 60_000,
      retry: false,
    }
  );
};

export type WhatsappSenderStatus = {
  configured: boolean;
  sender_status: string | null;
  was_online: boolean;
  quality: string | null;
  offline_reasons: Array<{ code?: string; message?: string }> | null;
};

// Live read on every call: the stored status is a snapshot, and a sender that went
// offline since would keep being charged for. The 15s poll is for an activation only.
export const useWhatsappSenderStatus = (
  customer_identification: string,
  entity_identification: string,
  enabled = true
) => {
  return useQuery<WhatsappSenderStatus>(
    ["whatsapp-sender-status", entity_identification],
    async () => {
      const response = await HttpService.get(
        `/${customer_identification}/${entity_identification}/user/whatsapp/sender/status`
      );
      return response?.data?.element;
    },
    {
      enabled: enabled && !!customer_identification && !!entity_identification,
      retry: false,
      refetchInterval: (data) =>
        !data || data.sender_status === "pending" ? 15000 : false,
      onSuccess: (data) => {
        if (data?.sender_status && data.sender_status !== "pending") {
          queryClient.invalidateQueries([
            "whatsapp-config",
            entity_identification,
          ]);
        }
      },
    }
  );
};

export const useSendWhatsApp = () => {
  return useMutation<SendWhatsAppProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      user_identification,
      data,
    }: SendWhatsAppProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/${user_identification}/whatsapp`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappCredit(
          variables.customer_identification,
          variables.entity_identification
        );
        toast.success("WhatsApp sent successfully");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendBulkWhatsApp = () => {
  return useMutation<SendWhatsAppProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendWhatsAppProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/whatsapp/bulk`,
        data
      ),
    {
      onSuccess: (value, variables) => {
        invalidateWhatsappCredit(
          variables.customer_identification,
          variables.entity_identification
        );
        warnOnPartialWhatsApp(value, "WhatsApp sent to all users successfully");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSendUsersBulkWhatsApp = () => {
  return useMutation<SendWhatsAppProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      group_identification,
      data,
    }: SendWhatsAppProps) =>
      HttpService.post(
        `${customer_identification}/${entity_identification}/user/group/${group_identification}/users/whatsapp/bulk`,
        data
      ),
    {
      onSuccess: (value, variables) => {
        invalidateWhatsappCredit(
          variables.customer_identification,
          variables.entity_identification
        );
        warnOnPartialWhatsApp(
          value,
          "WhatsApp sent to all selected users successfully"
        );
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

const invalidateWhatsappTemplates = (entity_identification: string) => {
  queryClient.invalidateQueries(["whatsapp-templates", entity_identification]);
  queryClient.invalidateQueries(["whatsapp-config", entity_identification]);
};

type WhatsappTemplateMutationProps = {
  customer_identification: string;
  entity_identification: string;
  template_id?: string;
  data?: Record<string, unknown>;
};

export const useGetWhatsappTemplates = (
  customer_identification: string,
  entity_identification: string,
  enabled = true
) => {
  return useQuery(
    ["whatsapp-templates", entity_identification],
    async () => {
      const response = await HttpService.get(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates`
      );
      return response?.data?.element;
    },
    {
      enabled: enabled && !!customer_identification && !!entity_identification,
      staleTime: 30_000,
      retry: false,
    }
  );
};

export const useCreateWhatsappTemplate = () => {
  return useMutation<WhatsappTemplateMutationProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: WhatsappTemplateMutationProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappTemplates(variables.entity_identification);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useUpdateWhatsappTemplate = () => {
  return useMutation<WhatsappTemplateMutationProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      template_id,
      data,
    }: WhatsappTemplateMutationProps) =>
      HttpService.put(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates/${template_id}`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappTemplates(variables.entity_identification);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useSubmitWhatsappTemplate = () => {
  return useMutation<WhatsappTemplateMutationProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      template_id,
    }: WhatsappTemplateMutationProps) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates/${template_id}/submit`,
        {}
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappTemplates(variables.entity_identification);
        toast.success("Template submitted for validation");
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useDeleteWhatsappTemplate = () => {
  return useMutation<WhatsappTemplateMutationProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      template_id,
    }: WhatsappTemplateMutationProps) =>
      HttpService.delete(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates/${template_id}`
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappTemplates(variables.entity_identification);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

type WhatsappFavoriteProps = {
  customer_identification: string;
  entity_identification: string;
  data: { template: string; favorite: boolean };
};

export const useSetWhatsappFavorite = () => {
  return useMutation<WhatsappFavoriteProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: WhatsappFavoriteProps) =>
      HttpService.put(
        `/${customer_identification}/${entity_identification}/user/whatsapp/favorites`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "whatsapp-config",
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

type WhatsappMappingProps = {
  customer_identification: string;
  entity_identification: string;
  data: {
    template: string;
    mapping: Record<
      string,
      { source: "text" | "field" | "path"; value: string; fallback?: string }
    >;
  };
};

/**
 * The payment links a client already holds. A payment template's button points
 * at one of them, so the merchant picks it instead of typing anything.
 */
export const useSetWhatsappReplyNotification = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    {
      customer_identification: string;
      entity_identification: string;
      enabled: boolean;
    }
  >(
    ({ customer_identification, entity_identification, enabled }) =>
      HttpService.put(
        `/${customer_identification}/${entity_identification}/user/whatsapp/reply-notification`,
        { enabled }
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "whatsapp-config",
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );

export const useRefreshWhatsappProfile = () =>
  useMutation<
    any,
    AxiosError<any, any>,
    { customer_identification: string; entity_identification: string }
  >(
    ({ customer_identification, entity_identification }) =>
      HttpService.post(
        `/${customer_identification}/${entity_identification}/user/whatsapp/profile/refresh`,
        {}
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "whatsapp-config",
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );

export const invalidateUserPaymentLinks = (
  entity_identification: string,
  user_identification: string
) => {
  queryClient.invalidateQueries([
    "user-payment-links",
    entity_identification,
    user_identification,
  ]);
};

export const useUserPaymentLinks = (
  customer_identification: string,
  entity_identification: string,
  user_identification: string,
  enabled: boolean
) => {
  return useQuery(
    ["user-payment-links", entity_identification, user_identification],
    async () => {
      const response = await PaymentModuleService.getAllPaymentRequests(
        customer_identification,
        entity_identification,
        user_identification
      );
      return (response.data?.element ?? [])
        .filter((link) => link.user_identification === user_identification)
        .sort(
          (a, b) =>
            new Date(b.payment_request_datetime ?? 0).getTime() -
            new Date(a.payment_request_datetime ?? 0).getTime()
        );
    },
    {
      enabled:
        enabled &&
        !!customer_identification &&
        !!entity_identification &&
        !!user_identification,
      staleTime: 60_000,
      retry: false,
    }
  );
};

/**
 * Resolve data paths for one recipient, in one guarded operation.
 *
 * The read routes carry no system-folder guard, so reading straight from them
 * would expose reserved data (a phone validation code, a licence key). The
 * coverage endpoint does carry that guard: it is asked FIRST, and nothing is
 * read unless it accepted the paths. Only its PERMISSION is used — its counting
 * does not see every column, and a wrong "absent" would be worse than no check
 * at all. The two steps live in one hook on purpose: exposing the read alone
 * would hand the next caller the hole this closes.
 */
export const useResolveWhatsappPaths = () =>
  useMutation<any, AxiosError<any, any>, any>(
    async ({
      customer_identification,
      entity_identification,
      user_identification,
      fields,
    }: {
      customer_identification: string;
      entity_identification: string;
      user_identification: string;
      fields: Array<{ root: string; value: string }>;
    }) => {
      const base = `/${customer_identification}/${entity_identification}/user`;
      await HttpService.post(`${base}/whatsapp/coverage`, {
        users: [user_identification],
        fields: fields.map((field) => ({
          source: "path",
          root: field.root,
          value: field.value,
        })),
      });

      const folders: Record<string, { read: boolean; value: any }> = {};
      for (const field of fields) {
        const folder = field.value.split(".")[0];
        const identity = `${field.root}|${folder}`;
        if (identity in folders) continue;
        const url =
          field.root === "user_data_preferences"
            ? `${base}/${user_identification}/preferences/${folder}`
            : `${base}/${user_identification}/data/${folder}`;
        try {
          const response: any = await HttpService.get(url);
          folders[identity] = {
            read: true,
            value: response?.data?.element?.[field.root]?.[folder],
          };
        } catch (error) {
          folders[identity] = {
            read: (error as AxiosError)?.response?.status === 404,
            value: undefined,
          };
        }
      }
      return folders;
    },
    { onError: () => {} }
  );

export type WhatsappCoverageField =
  | { source: "field"; value: string }
  | { source: "path"; root?: string; value: string }
  | { source: "form"; form_id: string | null; question: string };

/**
 * How many recipients actually hold a value for each dynamic source. The server
 * inspects a sample, so the answer says how many were checked.
 */
export const useWhatsappCoverage = (
  customer_identification: string,
  entity_identification: string,
  body: {
    group_identification?: string;
    users?: string[];
    fields: WhatsappCoverageField[];
  },
  enabled: boolean
) => {
  return useQuery(
    ["whatsapp-coverage", entity_identification, JSON.stringify(body)],
    async () => {
      const response = await HttpService.post(
        `/${customer_identification}/${entity_identification}/user/whatsapp/coverage`,
        body
      );
      return response?.data?.element;
    },
    {
      enabled:
        enabled &&
        !!customer_identification &&
        !!entity_identification &&
        body.fields.length > 0,
      staleTime: 60_000,
      retry: false,
    }
  );
};

export const useSaveWhatsappTemplateMapping = () => {
  return useMutation<WhatsappMappingProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      data,
    }: WhatsappMappingProps) =>
      HttpService.put(
        `/${customer_identification}/${entity_identification}/user/whatsapp/template-mapping`,
        data
      ),
    {
      onSuccess: (_value, variables) => {
        queryClient.invalidateQueries([
          "whatsapp-config",
          variables.entity_identification,
        ]);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};

export const useRefreshWhatsappTemplateStatus = () => {
  return useMutation<WhatsappTemplateMutationProps, AxiosError<any, any>, any>(
    ({
      customer_identification,
      entity_identification,
      template_id,
    }: WhatsappTemplateMutationProps) =>
      HttpService.get(
        `/${customer_identification}/${entity_identification}/user/whatsapp/templates/${template_id}/status`
      ),
    {
      onSuccess: (_value, variables) => {
        invalidateWhatsappTemplates(variables.entity_identification);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || err.message);
      },
    }
  );
};
